import { useMemo } from 'react'
import { DecodedValueMap } from 'use-query-params'

import { useTaskAssignees } from '../../hooks/useTaskAssignees'
import { CandidatesQueryParams } from '../CandidatesContainer.types'

export function useCandidatesEffectiveParams({
  programId,
  pageSize,
  queryParams
}: {
  programId: string
  pageSize: number
  queryParams: DecodedValueMap<CandidatesQueryParams>
}) {
  const { taskAssignees } = useTaskAssignees()

  const emailToId = useMemo(() => {
    if (!taskAssignees) return null

    return new Map(taskAssignees.map(a => [a.email.toLowerCase(), a.id]))
  }, [taskAssignees])

  const { assigneeEmails, includeUnassigned } = queryParams

  const baseParams = useMemo(() => {
    const { assigneeEmails: _assigneeEmails, includeUnassigned: _includeUnassigned, ...rest } = queryParams

    return { programId, pageSize, ...rest }
  }, [programId, pageSize, queryParams])

  return useMemo(() => {
    const urlAssigneeEmails = (assigneeEmails ?? []).filter((v): v is string => !!v)
    const hasAssigneeEmails = urlAssigneeEmails.length > 0

    if (!hasAssigneeEmails && !includeUnassigned) return baseParams

    const waitingForAssignees = hasAssigneeEmails && !emailToId

    const assigneeIdsForRequest =
      hasAssigneeEmails && emailToId
        ? urlAssigneeEmails.map(e => emailToId.get(e.toLowerCase())).filter((id): id is string => !!id)
        : undefined

    const includeUnassignedForRequest = !waitingForAssignees && includeUnassigned ? true : undefined

    return {
      ...baseParams,
      ...(assigneeIdsForRequest && assigneeIdsForRequest.length > 0 ? { assigneeIds: assigneeIdsForRequest } : {}),
      ...(includeUnassignedForRequest ? { includeUnassigned: includeUnassignedForRequest } : {})
    }
  }, [baseParams, emailToId, assigneeEmails, includeUnassigned])
}
